import { ProductSlugs } from '@deque/billing-utils';
import {
  Button,
  Combobox,
  ComboboxOption,
  Loader,
  Modal,
  ModalContent
} from '@deque/cauldron-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ContentToast from '~/modules/common/components/ContentToast';
import { getAuthUserReport } from '~/modules/common/utils/admin-client/auth-user-report';
import {
  EXPORT_PRODUCT_SLUGS,
  type ExportSlug
} from '~/modules/common/utils/admin-client/subscription-member-report';
import styles from './EnterpriseExplorer.css';

interface SubscriptionMemberReportModalProps {
  token: string;
  enterpriseId: string;
  onClose: () => void;
}

const SubscriptionMemberReportModal: React.FC<
  SubscriptionMemberReportModalProps
> = ({ token, enterpriseId, onClose }) => {
  const { t } = useTranslation();
  const [selectedProduct, setSelectedProduct] = useState<ExportSlug | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    csvContent: string;
    rowCount: number;
  } | null>(null);

  const productLabels: Record<ExportSlug, string> = useMemo(
    () => ({
      [ProductSlugs.axeDevToolsMobile]: t('Mobile'),
      [ProductSlugs.axeMonitor]: t('Monitor'),
      [ProductSlugs.axeAuditor]: t('Auditor'),
      [ProductSlugs.axeDevToolsExtension]: t('Axe Pro'),
      [ProductSlugs.axeDevToolsHTML]: t('DT-Web')
    }),
    [t]
  );

  const productOptions = useMemo(
    () =>
      EXPORT_PRODUCT_SLUGS.map(slug => ({
        label: productLabels[slug],
        value: slug
      })),
    [productLabels]
  );

  const handleGenerate = async () => {
    if (!selectedProduct) {
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const reportResult = await getAuthUserReport({
        token,
        enterpriseId,
        product: selectedProduct
      });
      setResult(reportResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!result || !selectedProduct) {
      return;
    }

    const blob = new Blob([result.csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `subscription-member-report-${enterpriseId}-${selectedProduct}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleProductSelection = ({ value }: { value: string | undefined }) => {
    setSelectedProduct((value as ExportSlug) ?? null);
    setResult(null);
    setError(null);
  };

  return (
    <Modal heading={t('Subscription Member Report')} onClose={onClose} show>
      <ModalContent>
        <Combobox
          label={t('Product')}
          value={selectedProduct ?? undefined}
          onSelectionChange={handleProductSelection}
        >
          {productOptions.map(option => (
            <ComboboxOption key={option.value} value={option.value}>
              {option.label}
            </ComboboxOption>
          ))}
        </Combobox>

        <div className={styles.reportActions}>
          <Button
            variant="primary"
            onClick={handleGenerate}
            disabled={!selectedProduct || loading}
          >
            {t('Generate Report')}
          </Button>
        </div>

        {loading && <Loader label={t('Generating report...')} tabIndex={-1} />}

        {error && (
          <ContentToast type="caution" show>
            <strong>{t('Error generating report -')}</strong>
            <code>{error}</code>
          </ContentToast>
        )}

        {result && (
          <>
            <p>
              {t('Report generated: {{count}} rows', {
                count: result.rowCount
              })}
            </p>
            <Button variant="secondary" onClick={handleDownload}>
              {t('Download CSV')}
            </Button>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default SubscriptionMemberReportModal;
